import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import RotateLeftIcon from '@mui/icons-material/RotateLeft'
import RotateRightIcon from '@mui/icons-material/RotateRight'
import { Box, Card, CardActions, Checkbox, Chip, IconButton, Tooltip, Typography } from '@mui/material'
import { EProcessingStatus } from '@src/types/automationDocument'
import { DocumentPage } from '@src/types/documentPage'
import { useTranslation } from 'react-i18next'

interface SortablePageCardProps {
  page: DocumentPage
  isEditMode: boolean
  isDragDisabled: boolean
  isSelected: boolean
  isSent?: boolean
  columns: number
  onToggleSelection: (pageId: string) => void
  onRotate: (pageId: string, degrees: number) => void
  onClick: (page: DocumentPage) => void
  pageHeader: (page: DocumentPage) => string
  assignDocumentTypeToPage?: (page: DocumentPage) => string | null
  assignDocumentTypeIsOverridden: (page: DocumentPage) => boolean
}

export const SortablePageCard = ({
  page,
  isEditMode,
  isDragDisabled,
  isSelected,
  isSent,
  columns,
  onToggleSelection,
  onRotate,
  onClick,
  pageHeader: getPageHeader,
  assignDocumentTypeToPage,
  assignDocumentTypeIsOverridden,
}: SortablePageCardProps) => {
  const { t } = useTranslation()
  const assigned = assignDocumentTypeToPage?.(page) ?? null
  const headerValue = getPageHeader(page)
  const isOverridden = assignDocumentTypeIsOverridden(page)

  const getCardHeight = () => {
    switch (columns) {
      case 1:
        return 600
      case 2:
        return 400
      case 3:
        return 300
      case 4:
        return 200
      default:
        return 200
    }
  }

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: page.id,
    disabled: isDragDisabled || !!isSent,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <Card
      ref={setNodeRef}
      style={style}
      data-tour="page-card"
      sx={{
        cursor: isEditMode && !isDragDisabled && !isSent ? 'move' : 'pointer',
        transition: 'transform 0.2s, box-shadow 0.2s, border 0.2s',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: isSelected ? 2 : 0,
        borderColor: isSelected ? 'primary.main' : 'transparent',
        opacity: 1,
        '&:hover': {
          transform: 'scale(1.02)',
          boxShadow: 4,
        },
      }}
      onClick={() => (isEditMode ? (!isSent && onToggleSelection(page.id)) : onClick(page))}
      {...attributes}
      {...listeners}
    >
      {isEditMode && !isSent && (
        <>
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              zIndex: 1,
            }}
          >
            <Checkbox
              size="small"
              checked={isSelected}
              onChange={() => onToggleSelection(page.id)}
              onClick={(e) => e.stopPropagation()}
            />
          </Box>
          {!isDragDisabled && (
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 1,
                cursor: 'move',
              }}
            >
              <DragIndicatorIcon fontSize="small" color="action" />
            </Box>
          )}
        </>
      )}
      {isSent && (
        <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
          <Chip
            icon={<CheckCircleIcon fontSize="small" />}
            label={t('docsAutomation.alreadySent' as 'common.close')}
            size="small"
            color="success"
            variant="outlined"
          />
        </Box>
      )}
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
        {page.storageUrl ? (
          <img
            src={page.storageUrl}
            alt={getPageHeader(page)}
            style={{
              width: 'auto',
              height: 'auto',
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
              transform: `rotate(${page.rotation || 0}deg)`,
              transition: 'transform 0.3s ease',
            }}
          />
        ) : (
          <Box
            sx={{
              width: '100%',
              height: getCardHeight(),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'grey.100',
            }}
          >
            <Typography color="text.secondary">No image</Typography>
          </Box>
        )}
      </Box>
      <CardActions
        sx={{
          p: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: isEditMode ? 'space-between' : 'center',
          gap: 1,
        }}
      >
        {isEditMode && !isSent ? (
          <>
            <Tooltip title={t('common.rotateLeft')}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation()
                  onRotate(page.id, -90)
                }}
              >
                <RotateLeftIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Box sx={{ flex: 1, textAlign: 'center' }}>
              {assigned && isOverridden && (
                <Typography variant="caption" sx={{ display: 'block', fontWeight: 'bold' }}>
                  {assigned}
                </Typography>
              )}
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  color: page.processingStatus === EProcessingStatus.Discarded || isOverridden ? 'error.main' : 'inherit',
                }}
              >
                {headerValue}
              </Typography>
            </Box>
            <Tooltip title={t('common.rotateRight')}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation()
                  onRotate(page.id, 90)
                }}
              >
                <RotateRightIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        ) : (
          <Box sx={{ textAlign: 'center' }}>
            {assigned && isOverridden && (
              <Typography variant="caption" sx={{ display: 'block', fontWeight: 'bold' }}>
                {assigned}
              </Typography>
            )}
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: page.processingStatus === EProcessingStatus.Discarded || isOverridden ? 'error.main' : 'inherit',
              }}
            >
              {headerValue}
            </Typography>
          </Box>
        )}
      </CardActions>
    </Card>
  )
}
